/**
 * @module useAuth
 * @description
 * Authentication hook and context provider for PlantQR.
 * 
 * This module provides:
 * - Session-based authentication management
 * - User state management across the application
 * - Login, register, and logout functionality
 * - Password reset flow
 * - Automatic session restoration on app load
 * 
 * Domain Knowledge:
 * - Uses session tokens stored in localStorage
 * - Automatically refreshes auth state on mount
 * - Handles complex error responses from registration
 * - Supports invite code system for registration
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  User,
  LoginCredentials,
  RegisterCredentials,
  AuthResponse,
  PasswordResetRequest,
  PasswordReset,
} from '../types/auth';
import { api } from '../lib/api';

/**
 * Authentication context type defining all auth-related functions and state.
 */
interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (credentials: RegisterCredentials) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  updateUser: (updatedUser: User) => void;
  requestPasswordReset: (request: PasswordResetRequest) => Promise<{ message: string }>;
  resetPassword: (reset: PasswordReset) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Authentication provider component that manages auth state for the entire app.
 * 
 * @param children - React components to wrap with auth context
 * 
 * @remarks
 * - Automatically attempts to restore session on mount
 * - Provides auth state and methods to all child components
 * - Handles token storage and removal
 * 
 * @example
 * ```tsx
 * // In App.tsx
 * <AuthProvider>
 *   <Router>
 *     <Routes>...</Routes>
 *   </Router>
 * </AuthProvider>
 * ```
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  /**
   * Log in a user with email and password.
   * 
   * @param credentials - Login credentials (email and password)
   * @throws Error if login fails
   * 
   * @remarks
   * - Stores session token in localStorage on success
   * - Updates user state for immediate UI updates
   */
  const login = async (credentials: LoginCredentials) => {
    try {
      const response = await api.post<AuthResponse>('/auth/login', credentials);

      if (response.data.success && response.data.data) {
        const { user, sessionToken } = response.data.data;
        setUser(user);
        localStorage.setItem('sessionToken', sessionToken);
      } else {
        throw new Error(response.data.error || 'Login failed');
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Login failed';
      throw new Error(message);
    }
  };

  /**
   * Register a new user account.
   * 
   * @param credentials - Registration data including name, email, password, and invite code
   * @throws Error if registration fails
   * 
   * @remarks
   * - Requires valid invite code for registration
   * - Automatically logs in user after successful registration
   * - Provides detailed error messages for validation failures
   */
  const register = async (credentials: RegisterCredentials) => {
    try {
      const response = await api.post<AuthResponse>('/auth/register', credentials);

      if (response.data.success && response.data.data) {
        const { user, sessionToken } = response.data.data;
        setUser(user);
        localStorage.setItem('sessionToken', sessionToken);
      } else {
        throw new Error(response.data.error || 'Registration failed');
      }
    } catch (error: unknown) {
      // Handle axios errors with response data
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { error?: string; details?: string } } };
        if (axiosError.response?.data?.error) {
          throw new Error(axiosError.response.data.error);
        } else if (axiosError.response?.data?.details) {
          throw new Error(axiosError.response.data.details);
        }
      } else if (error instanceof Error) {
        throw new Error(error.message);
      } else {
        throw new Error('Registration failed');
      }
    }
  };

  /**
   * Log out the current user.
   * 
   * @remarks
   * - Clears session token from localStorage
   * - Makes API call to invalidate server session
   * - Resets user state to null
   */
  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Continue with logout even if API call fails
    } finally {
      setUser(null);
      localStorage.removeItem('sessionToken');
    }
  };

  /**
   * Refresh authentication state from stored session token.
   * 
   * @remarks
   * - Called automatically on component mount
   * - Validates stored token with server
   * - Removes invalid tokens from localStorage
   * - Sets loading state while checking auth
   */
  const refreshAuth = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.get('/auth/me');

      if (response.data.success && response.data.data) {
        setUser(response.data.data); // data is the user object
      } else {
        localStorage.removeItem('sessionToken');
      }
    } catch {
      localStorage.removeItem('sessionToken');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Update the current user object in state.
   * 
   * @param updatedUser - Updated user data
   * 
   * @remarks
   * Used when user profile is updated to reflect changes immediately
   * without requiring a full auth refresh.
   */
  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
  };

  /**
   * Request a password reset email.
   * 
   * @param request - Email address for password reset
   * @returns Success message from server
   * @throws Error if request fails
   * 
   * @remarks
   * Sends password reset link to user's email with 1-hour expiration
   */
  const requestPasswordReset = async (request: PasswordResetRequest) => {
    try {
      const response = await api.post('/auth/request-password-reset', request);

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Password reset request failed');
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Password reset request failed';
      throw new Error(message);
    }
  };

  /**
   * Reset password using token from email.
   * 
   * @param reset - Token and new password
   * @throws Error if reset fails
   * 
   * @remarks
   * - Token must be valid and not expired
   * - Automatically logs in user after successful reset
   */
  const resetPassword = async (reset: PasswordReset) => {
    try {
      const response = await api.post('/auth/reset-password', reset);

      if (!response.data.success) {
        throw new Error(response.data.error || 'Password reset failed');
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Password reset failed';
      throw new Error(message);
    }
  };

  useEffect(() => {
    refreshAuth();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated,
        login,
        register,
        logout,
        refreshAuth,
        updateUser,
        requestPasswordReset,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Hook to access authentication context.
 * 
 * @returns Authentication context with user state and auth methods
 * @throws Error if used outside of AuthProvider
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { user, isAuthenticated, login, logout } = useAuth();
 *   
 *   if (!isAuthenticated) {
 *     return <LoginForm onSubmit={login} />;
 *   }
 *   
 *   return (
 *     <div>
 *       <p>Welcome, {user.name}!</p>
 *       <button onClick={logout}>Logout</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
